/**
 * Who may use each in-room menu item or control (SC-439).
 *
 * Routes declare access in `config/routeMetadata.ts` and page controls through
 * `types/rbac/surfaces.ts`. The in-room chrome (bottom toolbar, call controls, profile menu, room
 * owner menu, player popup, picture-in-picture window) declared nothing, so a guard test could not
 * tell a deliberate "everyone" from a forgotten gate. Every item now carries one of the
 * declarations below, and `__tests__/menuAccess.guard.test.ts` fails when one is missing.
 *
 * A declaration states today's behaviour; it does not enforce anything. Visibility is still decided
 * by each item's `shouldBeVisible` (or by the component), and the guard test checks the two agree.
 * In particular the camera, microphone, screen share and chat are not gated by `video.use`,
 * `screenshare.use` or `chat.use` yet (user decision, 2026-10-24): they declare the class that
 * matches who sees them today.
 */
import type { PermissionKey } from '../../../../types/rbac/catalogue'

export type MenuAccess =
  /** Members and guests both see it. */
  | { kind: 'everyone' }
  /** Signed-in members only; guests never see it. */
  | { kind: 'member' }
  /** Guests only. */
  | { kind: 'guest' }
  /**
   * Whoever owns the virtual room they are standing in. Ownership is room state, not a role, and
   * the code does not exclude guests, so a guest who owns a room sees it.
   */
  | { kind: 'roomOwner' }
  /** Platform admin attested by the server (`useElevatedAdmin`), never the role string. */
  | { kind: 'platformAdmin' }
  /**
   * Follows a catalogue permission, through the surface in `types/rbac/surfaces.ts` that names the
   * same key. `alsoFor` lists the legacy roles that see it without holding the key. Guests never.
   */
  | {
      kind: 'permission'
      key: PermissionKey
      surface: string
      alsoFor: readonly ('platformAdmin' | 'moderator')[]
    }

export const MENU_ACCESS_KINDS = [
  'everyone',
  'member',
  'guest',
  'roomOwner',
  'platformAdmin',
  'permission',
] as const satisfies readonly MenuAccess['kind'][]

/** Whether a guest in the room is shown the item (the answer the declaration commits to). */
export function guestsSee(access: MenuAccess): boolean {
  return access.kind === 'everyone' || access.kind === 'guest' || access.kind === 'roomOwner'
}

const EVERYONE: MenuAccess = { kind: 'everyone' }
const MEMBER: MenuAccess = { kind: 'member' }
const ROOM_OWNER: MenuAccess = { kind: 'roomOwner' }

/**
 * The controls that are written as JSX rather than built from a `MenuItemInfo` list. Each one is
 * tagged in its component with `menuControlAttrs('<id>')`, and the guard test counts those tags
 * against the interactive elements in each file, so a new button cannot go in unnoticed.
 */
export const IN_ROOM_CONTROL_ACCESS = {
  // Call controls (GameMenuCallControls, its connecting placeholder and the PiP window mirror).
  'call.mic': EVERYONE,
  'call.mic-device': EVERYONE,
  'call.camera': EVERYONE,
  'call.camera-device': EVERYONE,
  'call.background-effects': EVERYONE,
  'call.background-warning': EVERYONE,
  'call.background-warning-action': EVERYONE,
  'call.screen-share': EVERYONE,
  'call.raise-hand': EVERYONE,
  'call.layout': EVERYONE,
  'call.layout-option': EVERYONE,
  'call.picture-in-picture': EVERYONE,
  'pip.auto-open': EVERYONE,
  'pip.back-to-tab': EVERYONE,
  'pip.close': EVERYONE,
  // Profile menu: the status block is hidden for guests (`!isGuest`).
  'profile.status-online': MEMBER,
  'profile.status-away': MEMBER,
  'profile.status-do-not-disturb': MEMBER,
  'profile.status-icon': MEMBER,
  'profile.status-text': MEMBER,
  // Room options menu (RoomButton): shown only to the owner of the room.
  'room.options': ROOM_OWNER,
  'room.transfer-ownership': ROOM_OWNER,
  'room.password': ROOM_OWNER,
  'room.reserve': ROOM_OWNER,
  'room.retry-password': ROOM_OWNER,
  'room.admit-guests': ROOM_OWNER,
  'room.end-guest-meeting': ROOM_OWNER,
  // Click-a-player popup: GameOverlayUI does not open it for guests.
  'player.teleport': MEMBER,
  'player.follow': MEMBER,
  'player.teleport-guest': MEMBER,
  'player.escort': MEMBER,
} as const satisfies Record<string, MenuAccess>

export type InRoomControlId = keyof typeof IN_ROOM_CONTROL_ACCESS

/** Marks an element with the id of its declaration, so the control can be found and audited. */
export function menuControlAttrs(id: InRoomControlId): { 'data-menu-control': InRoomControlId } {
  return { 'data-menu-control': id }
}

/** Same, for a button drawn from a `MenuItemInfo` (its `access` is on the item itself). */
export function menuItemAttrs(item: { id: string }): { 'data-menu-item': string } {
  return { 'data-menu-item': item.id }
}
